import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import { Rocket, ExternalLink, AlertCircle, CheckCircle2, Copy, Check, Loader2, Share2 } from 'lucide-react';
import { ExternalFavicon } from '@/components/ExternalFavicon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDeploySettings } from '@/hooks/useDeploySettings';
import { useProjectDeploySettings } from '@/hooks/useProjectDeploySettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFS } from '@/hooks/useFS';
import { useAppContext } from '@/hooks/useAppContext';
import { useNostr } from '@nostrify/react';
import { useGitStatus } from '@/hooks/useGitStatus';
import { ShakespeareAdapter, NetlifyAdapter, VercelAdapter, NsiteAdapter, DeployAdapter } from '@/lib/deploy';
import { Link } from 'react-router-dom';
import type { DeployProvider, ShakespeareDeployProvider, NetlifyProvider, VercelProvider } from '@/contexts/DeploySettingsContext';
import { ShakespeareDeployForm } from '@/components/deploy/ShakespeareDeployForm';
import { NetlifyDeployForm } from '@/components/deploy/NetlifyDeployForm';
import { VercelDeployForm } from '@/components/deploy/VercelDeployForm';
import { NsiteDeployForm } from '@/components/deploy/NsiteDeployForm';
import { ShareProjectDialog } from '@/components/ShareProjectDialog';

/**
 * Helper function to get provider URL for favicon
 */
function getProviderUrl(provider: DeployProvider): string | null {
  switch (provider.type) {
    case 'shakespeare':
      if (provider.host) {
        return `https://${provider.host}`;
      }
      return 'https://shakespeare.diy';

    case 'netlify':
      if (provider.baseURL) {
        return provider.baseURL;
      }
      return 'https://netlify.com';

    case 'vercel':
      if (provider.baseURL) {
        return provider.baseURL;
      }
      return 'https://vercel.com';

    case 'nsite':
      return null;

    default:
      return null;
  }
}

/**
 * Render provider icon using favicon or fallback
 */
function renderProviderIcon(provider: DeployProvider, size = 14) {
  const url = getProviderUrl(provider);

  if (url) {
    return (
      <ExternalFavicon
        url={url}
        size={size}
        fallback={<Rocket size={size} className="text-muted-foreground" />}
      />
    );
  }

  return <Rocket size={size} className="text-muted-foreground" />;
}

interface DeployDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShakespeareFormData {
  subdomain: string;
}

interface NsiteFormData {
  nsec: string;
}

interface NetlifyFormData {
  siteId: string;
  siteName: string;
}

interface VercelFormData {
  projectName: string;
  teamId: string;
}

export function DeployDialog({ projectId, projectName, open, onOpenChange }: DeployDialogProps) {
  const { t } = useTranslation();
  const { settings } = useDeploySettings();
  const { settings: projectSettings, updateSettings: updateProjectSettings, saveSettings } = useProjectDeploySettings(projectId);
  const { user } = useCurrentUser();
  const { fs } = useFS();
  const { config } = useAppContext();
  const { nostr } = useNostr();
  const { data: gitStatus } = useGitStatus(projectId);

  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<{ url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Provider-specific form data
  const [shakespeareForm, setShakespeareForm] = useState<ShakespeareFormData>({
    subdomain: projectId,
  });
  const [nsiteForm, setNsiteForm] = useState<NsiteFormData>({
    nsec: '',
  });
  const [netlifyForm, setNetlifyForm] = useState<NetlifyFormData>({
    siteId: '',
    siteName: '',
  });
  const [vercelForm, setVercelForm] = useState<VercelFormData>({
    projectName: projectName || projectId,
    teamId: '',
  });

  // Badge settings
  const [showBadge, setShowBadge] = useState(false);

  // Load project settings when dialog opens
  useEffect(() => {
    if (open) {
      // First, try to use the currentProvider from project settings
      if (projectSettings.currentProvider && settings.providers.some(p => p.id === projectSettings.currentProvider)) {
        setSelectedProviderId(projectSettings.currentProvider);
      } else {
        // Otherwise, find the first configured provider in project settings
        const providerIds = Object.keys(projectSettings.providers);
        if (providerIds.length > 0) {
          setSelectedProviderId(providerIds[0]);
        }
      }
      // Load badge preference
      setShowBadge(projectSettings.showBadge ?? false);
    }
  }, [open, projectSettings, settings.providers]);

  const selectedProvider = settings.providers.find(p => p.id === selectedProviderId);

  const handleCopyUrl = async () => {
    if (deployResult?.url) {
      await navigator.clipboard.writeText(deployResult.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeploy = async () => {
    if (!selectedProvider) return;

    setIsDeploying(true);
    setError(null);
    setDeployResult(null);

    try {
      const projectPath = `/projects/${projectId}`;

      let adapter: DeployAdapter;

      if (selectedProvider.type === 'shakespeare') {
        if (!user) {
          throw new Error('You must be logged in with Nostr to use Shakespeare Deploy');
        }

        const shakespeareProvider = selectedProvider as ShakespeareDeployProvider;
        adapter = new ShakespeareAdapter({
          fs,
          signer: user.signer,
          host: shakespeareProvider.host,
          subdomain: shakespeareForm.subdomain || undefined,
          corsProxy: shakespeareProvider.proxy ? config.corsProxy : undefined,
        });
      } else if (selectedProvider.type === 'nsite') {
        const nsiteProvider = selectedProvider as import('@/contexts/DeploySettingsContext').NsiteProvider;

        if (!nsiteForm.nsec) {
          throw new Error('Site private key (nsec) is required');
        }

        adapter = new NsiteAdapter({
          fs,
          nostr,
          nsec: nsiteForm.nsec,
          gateway: nsiteProvider.gateway,
          relayUrls: nsiteProvider.relayUrls,
          blossomServers: nsiteProvider.blossomServers,
        });
      } else if (selectedProvider.type === 'netlify') {
        const netlifyProvider = selectedProvider as NetlifyProvider;
        if (!netlifyProvider.apiKey) {
          throw new Error('Netlify API key is required');
        }

        // If siteId is empty, we're creating a new site, so only pass siteName
        // If siteId is set, we're updating an existing site
        adapter = new NetlifyAdapter({
          fs,
          apiKey: netlifyProvider.apiKey,
          baseURL: netlifyProvider.baseURL,
          siteName: netlifyForm.siteId ? undefined : (netlifyForm.siteName || undefined),
          siteId: netlifyForm.siteId || undefined,
          corsProxy: netlifyProvider.proxy ? config.corsProxy : undefined,
        });
      } else if (selectedProvider.type === 'vercel') {
        const vercelProvider = selectedProvider as VercelProvider;
        if (!vercelProvider.apiKey) {
          throw new Error('Vercel API key is required');
        }

        adapter = new VercelAdapter({
          fs,
          apiKey: vercelProvider.apiKey,
          baseURL: vercelProvider.baseURL,
          teamId: vercelForm.teamId || undefined,
          projectName: vercelForm.projectName || undefined,
          corsProxy: vercelProvider.proxy ? config.corsProxy : undefined,
        });
      } else {
        throw new Error('Unknown provider type');
      }

      // Get repository URL from git remotes for clone feature
      const repositoryUrl = gitStatus?.remotes?.find(r => r.name === 'origin')?.url;

      // Save badge and repository settings before deploy
      // This allows the build system to inject the badge if enabled
      const newSettings = {
        ...projectSettings,
        showBadge,
        repositoryUrl,
      };
      await saveSettings(newSettings);

      // If badge is enabled, we need to rebuild to inject the badge
      if (showBadge) {
        const { buildProject } = await import('@/lib/build');
        await buildProject({
          fs,
          projectPath,
          domParser: new DOMParser(),
          esmUrl: config.esmUrl,
        });
      }

      const result = await adapter.deploy({
        projectId,
        projectPath,
      });

      // Save project-specific settings (updateSettings now automatically sets currentProvider)
      if (selectedProvider.type === 'shakespeare') {
        await updateProjectSettings(selectedProviderId, {
          type: 'shakespeare',
          data: {
            subdomain: shakespeareForm.subdomain || undefined,
            showBadge,
          },
        });
      } else if (selectedProvider.type === 'nsite') {
        await updateProjectSettings(selectedProviderId, {
          type: 'nsite',
          data: {
            nsec: nsiteForm.nsec,
          },
        });
      } else if (selectedProvider.type === 'netlify') {
        await updateProjectSettings(selectedProviderId, {
          type: 'netlify',
          data: {
            siteId: result.metadata?.siteId as string | undefined,
          },
        });
      } else if (selectedProvider.type === 'vercel') {
        await updateProjectSettings(selectedProviderId, {
          type: 'vercel',
          data: {
            teamId: vercelForm.teamId || undefined,
            projectId: vercelForm.projectName || undefined,
          },
        });
      }

      setDeployResult(result);

      // Save the deploy URL for later use
      await saveSettings({
        ...projectSettings,
        lastDeployUrl: result.url,
      });

      // Celebrate with confetti! 🎉
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 99999 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval = window.setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        // Confetti from both sides
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        });
      }, 250);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deployment failed');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleClose = () => {
    // Only reset if we're not showing the success result
    // This prevents the dialog from getting stuck
    if (!deployResult) {
      setSelectedProviderId('');
      setError(null);
      setCopied(false);
      // Reset forms
      setShakespeareForm({ subdomain: projectId });
      setNsiteForm({ nsec: '' });
      setNetlifyForm({ siteId: '', siteName: '' });
      setVercelForm({ projectName: projectName || projectId, teamId: '' });
      setShowBadge(false);
    } else {
      // Clear the result when closing after successful deployment
      setDeployResult(null);
    }
    onOpenChange(false);
  };

  const handleShakespeareSubdomainChange = useCallback((subdomain: string) => {
    setShakespeareForm({ subdomain });
  }, []);

  const handleNsiteNsecChange = useCallback((nsec: string) => {
    setNsiteForm({ nsec });
  }, []);

  const handleNetlifySiteChange = useCallback((siteId: string, siteName: string) => {
    setNetlifyForm({ siteId, siteName });
  }, []);

  const handleVercelConfigChange = useCallback((projectName: string, teamId: string) => {
    setVercelForm({ projectName, teamId });
  }, []);

  const renderProviderFields = () => {
    if (!selectedProvider) return null;

    if (selectedProvider.type === 'shakespeare') {
      const shakespeareProvider = selectedProvider as ShakespeareDeployProvider;

      return (
        <ShakespeareDeployForm
          host={shakespeareProvider.host}
          projectId={projectId}
          onSubdomainChange={handleShakespeareSubdomainChange}
        />
      );
    }

    if (selectedProvider.type === 'nsite') {
      const savedConfig = projectSettings.providers[selectedProviderId];
      const savedNsec = savedConfig?.type === 'nsite' ? savedConfig.data.nsec : undefined;

      return (
        <NsiteDeployForm
          gateway={selectedProvider.gateway}
          savedNsec={savedNsec}
          onNsecChange={handleNsiteNsecChange}
        />
      );
    }

    if (selectedProvider.type === 'netlify') {
      const savedConfig = projectSettings.providers[selectedProviderId];
      const savedSiteId = savedConfig?.type === 'netlify' ? savedConfig.data.siteId : undefined;

      return (
        <NetlifyDeployForm
          apiKey={selectedProvider.apiKey}
          baseURL={selectedProvider.baseURL}
          projectId={projectId}
          projectName={projectName}
          savedSiteId={savedSiteId}
          onSiteChange={handleNetlifySiteChange}
          corsProxy={selectedProvider.proxy ? config.corsProxy : undefined}
        />
      );
    }

    if (selectedProvider.type === 'vercel') {
      const savedConfig = projectSettings.providers[selectedProviderId];
      const savedTeamId = savedConfig?.type === 'vercel' ? savedConfig.data.teamId : undefined;
      const savedProjectName = savedConfig?.type === 'vercel' ? savedConfig.data.projectId : undefined;

      return (
        <VercelDeployForm
          projectId={projectId}
          projectName={projectName}
          savedTeamId={savedTeamId}
          savedProjectName={savedProjectName}
          onConfigChange={handleVercelConfigChange}
        />
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2.5 text-lg">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
              <Rocket className="h-4 w-4 text-primary" />
            </div>
            {t('deployProject')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('deployProjectDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5">
          {deployResult ? (
            <div className="space-y-5">
              {/* Success State */}
              <div className="flex flex-col items-center text-center py-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 mb-4">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="text-base font-medium mb-1">
                  {t('deploymentSuccessful')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('projectDeployedSuccessfully')}
                </p>
              </div>

              {/* URL Display */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('liveUrl')}</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      value={deployResult.url}
                      readOnly
                      className="pr-10 font-mono text-sm bg-muted/50 border-muted-foreground/20"
                    />
                  </div>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleCopyUrl}
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => window.open(deployResult.url, '_blank')}
                    className="shrink-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  {t('done')}
                </Button>
                <Button
                  onClick={() => setShareDialogOpen(true)}
                  className="flex-1 gap-2"
                >
                  <Share2 className="h-4 w-4" />
                  {t('share')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {settings.providers.length === 0 ? (
                <div className="text-center py-8">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted mx-auto mb-4">
                    <AlertCircle className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-medium mb-1">
                    {t('noProvidersConfigured')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('configureDeployProviderFirst')}
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/settings/deploy" onClick={handleClose}>
                      {t('configureDeploySettings')}
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  {/* Provider Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('selectProvider')}</Label>
                    <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                      <SelectTrigger className="h-10 bg-background">
                        {selectedProvider ? (
                          <div className="flex items-center gap-2.5">
                            {renderProviderIcon(selectedProvider, 16)}
                            <span className="font-medium">{selectedProvider.name}</span>
                          </div>
                        ) : (
                          <SelectValue placeholder={t('chooseDeploymentProvider')} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {settings.providers.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            <div className="flex items-center gap-2.5">
                              {renderProviderIcon(provider, 16)}
                              <span>{provider.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Provider-specific fields */}
                  {selectedProvider && (
                    <div className="space-y-4 pt-1">
                      {renderProviderFields()}
                    </div>
                  )}

                  {/* Badge opt-in */}
                  {selectedProvider && (
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 bg-muted/30">
                      <Checkbox
                        id="show-badge"
                        checked={showBadge}
                        onCheckedChange={(checked) => setShowBadge(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="show-badge"
                          className="text-sm font-medium cursor-pointer leading-none"
                        >
                          {t('showAfterHoursBadge')}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t('showAfterHoursBadgeDescription')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Nostr login warning */}
                  {selectedProvider?.type === 'shakespeare' && !user && (
                    <Alert className="bg-amber-500/10 border-amber-500/20">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <AlertDescription className="text-sm">
                        {t('nostrLoginRequiredForShakespeare')}{' '}
                        <Link
                          to="/settings/nostr"
                          className="font-medium underline underline-offset-2 hover:no-underline"
                          onClick={handleClose}
                        >
                          {t('goToNostrSettings')}
                        </Link>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Error display */}
                  {error && (
                    <Alert variant="destructive" className="bg-destructive/10">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Action buttons */}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={handleClose}>
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={handleDeploy}
                      disabled={
                        !selectedProvider ||
                        isDeploying ||
                        (selectedProvider.type === 'shakespeare' && !user) ||
                        (selectedProvider.type === 'nsite' && !nsiteForm.nsec) ||
                        (selectedProvider.type === 'netlify' && !netlifyForm.siteId && !netlifyForm.siteName)
                      }
                      className="gap-2 min-w-[100px]"
                    >
                      {isDeploying ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('deploying')}
                        </>
                      ) : (
                        <>
                          <Rocket className="h-4 w-4" />
                          {t('deploy')}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Share Dialog */}
      <ShareProjectDialog
        projectId={projectId}
        projectName={projectName}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
    </Dialog>
  );
}
